<template>
  <div>
    <Loading :isActive="carregando" />

    <!--
      O aviso fica na tela depois de o alerta ser fechado: com as duas listas
      vazias por erro de carga, o catálogo parece não ter conectores e as
      configuradas parecem não existir.
    -->
    <CAlert v-if="erro" show color="danger" class="alerta-carga">
      <span>{{ erro }}</span>
      <CButton color="danger" variant="outline" size="sm" @click="carregar">
        Tentar novamente
      </CButton>
    </CAlert>

    <CTabs variant="tabs" class="nav-tabs-boxed" :active-tab.sync="abaAtiva">
      <CTab :title="tituloConfiguradas">
        <IntegracoesConfiguradas
          :integracoes="integracoes"
          @configurar="abrirConfiguracao"
          @recarregar="carregar"
          @ir-para-marketplace="abaAtiva = ABA_CATALOGO"
        />
      </CTab>

      <!--
        "Catálogo" e não "Marketplace": o segundo sugere integração com Mercado
        Livre, Magalu e afins — que é outra coisa, e um dia pode ser um conector
        desta mesma lista.
      -->
      <CTab title="Catálogo">
        <Marketplace :conectores="conectores" @instalar="instalar" />
      </CTab>
    </CTabs>
  </div>
</template>

<script>
/*
 * Módulo de Integrações: o que já está configurado e o que pode ser instalado.
 *
 * A tela carrega as duas listas de uma vez porque o Marketplace precisa saber
 * quantas instâncias cada conector já tem (é o selo "Instalada"), e a aba de
 * configuradas precisa dos dados do conector para montar o card. Duas chamadas
 * numa carga só evitam o vaivém a cada troca de aba.
 */
import { get } from '@/helpers/apiRequest';
import { loadDescs } from '@/helpers/utils';
import IntegracoesConfiguradas from './IntegracoesConfiguradas';
import Marketplace from './Marketplace';

/*
 * A aba escolhida vive na URL.
 *
 * Sem isso, voltar da tela de uma integração sempre caía nas configuradas —
 * inclusive para quem tinha acabado de sair do catálogo, que era de onde ele
 * veio. O nome na query, e não o índice: `?aba=catalogo` sobrevive a uma aba
 * nova no meio, um `?aba=1` não.
 */
const ABAS = ['configuradas', 'catalogo'];

const ABA_CATALOGO = 1;

export default {
  name: 'Integracoes',
  components: { IntegracoesConfiguradas, Marketplace },
  props: {
    /*
     * A aba de abertura, pelo nome declarado em ABAS — a rota
     * /integracoes/marketplace entra direto no catálogo. Nome e não índice:
     * com o número, inserir uma aba no meio da lista mudaria para onde a rota
     * leva sem ninguém perceber.
     */
    abaInicial: {
      type: String,
      default: 'configuradas',
    },
  },
  data() {
    return {
      ABA_CATALOGO,
      abaAtiva: this.abaDaRota(),
      // O título vem do módulo, como no resto do sistema: o cliente pode
      // renomeá-lo, e o menu lateral já mostra esse mesmo nome.
      descricaoModulo: 'Integrações',
      integracoes: [],
      conectores: [],
      carregando: true,
      erro: '',
    };
  },
  computed: {
    tituloConfiguradas() {
      if (!this.integracoes.length) {
        return `${this.descricaoModulo} configuradas`;
      }

      return `${this.descricaoModulo} configuradas (${this.integracoes.length})`;
    },
  },
  watch: {
    /*
     * `replace` e não `push`: alternar entre as duas abas não é navegação que
     * mereça uma entrada no histórico — se fosse, voltar do navegador ficaria
     * preso trocando de aba em vez de sair da tela.
     */
    abaAtiva(indice) {
      const aba = ABAS[indice] || ABAS[0];

      if (this.$route.query.aba !== aba) {
        this.$router.replace({ path: '/integracoes', query: { aba } }).catch(() => {});
      }
    },
  },
  methods: {
    /**
     * A aba a abrir: a da URL, a da rota dedicada (/integracoes/marketplace) ou
     * a primeira.
     */
    abaDaRota() {
      const daQuery = ABAS.indexOf(this.$route.query.aba);

      if (daQuery >= 0) {
        return daQuery;
      }

      return Math.max(ABAS.indexOf(this.abaInicial), 0);
    },

    /**
     * A aba corrente, para quem sai daqui poder voltar a ela.
     */
    abaCorrente() {
      return ABAS[this.abaAtiva] || ABAS[0];
    },

    carregar() {
      this.carregando = true;

      this.erro = '';

      Promise.all([get('/admin/integracoes'), get('/admin/integracoes/catalogo')])
        .then(([integracoes, conectores]) => {
          this.integracoes = integracoes || [];
          this.conectores = conectores || [];
          this.erro = '';
        })
        .catch((erro) => {
          this.erro = (erro && erro.message)
            || 'Não foi possível carregar as integrações.';

          this.$swal({
            title: 'Não foi possível carregar as integrações',
            text: erro.message,
            icon: 'error',
          });
        })
        .finally(() => {
          this.carregando = false;
        });
    },

    carregarDescricao() {
      try {
        this.descricaoModulo = loadDescs('ADMIN_INTEGRACAO').descPlural;
      } catch (erro) {
        // Módulo ausente na carga (permissão recém-criada, sessão antiga):
        // o rótulo padrão serve, e a tela não precisa quebrar por causa dele.
      }
    },

    abrirConfiguracao(integracao, opcoes) {
      /*
       * `replicar` leva a tela de configuração a abrir já no bloco de cópia
       * para outras finalidades — o atalho vem do cartão agrupado, onde se
       * percebe que uma finalidade ficou sem servidor.
       */
      this.$router.push({
        path: `/integracoes/${integracao.id}/configurar`,
        query: {
          ...(opcoes && opcoes.replicar ? { replicar: '1' } : {}),
          origem: this.abaCorrente(),
        },
      });
    },

    /*
     * Conector de instância única já instalado não abre uma tela de instalação
     * nova — leva direto para a configuração existente, que é o que o usuário
     * quer quando clica nele no Marketplace.
     */
    instalar(conector) {
      if (!conector.multi_instancia && conector.instalacoes > 0) {
        const existente = this.integracoes.find((i) => i.catalogo_slug === conector.slug);

        if (existente) {
          this.$router.push({
            path: `/integracoes/${existente.id}/configurar`,
            query: { origem: this.abaCorrente() },
          });

          return;
        }
      }

      this.$router.push({
        path: `/integracoes/instalar/${conector.slug}`,
        query: { origem: this.abaCorrente() },
      });
    },
  },
  beforeMount() {
    this.carregarDescricao();
    this.carregar();
  },
};
</script>

<style scoped>
.alerta-carga {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
</style>

